import Templates from '@elisa-luukku-sites/cms-templates';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useIdObserver } from '../../components/Impression/useIdObserver.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useSessionData } from '../../public/site/SessionDataProvider.js';
import { useOnlineModelsContext } from '../../public/site/onlineModel/onlineModelContextProvider.js';
import { getFilteredHeaders } from '../../public/site/onlineModel/onlineModelService.js';
import { employeePromotionToProductGridProduct, toProductGridProduct } from '../common/cmsTemplatesUtils.js';
import type { OnlineModelHeader } from '../../generated/api/models.js';
import type { PromotionBlockData } from '../cmsSchema.js';
import type { ProductGridProduct } from '@elisa-luukku-sites/cms-templates/types/src/utils/onlinefirst.js';

import './PromotionBlock.scss';

interface PromotionBlockContainerProps extends PromotionBlockData {
  isEmployeePromotionBlock?: boolean;
}

const SiteTemplates = Templates.default;

export const PromotionBlockContainer = ({
  title,
  onlineModelHeaders,
  promotions = [],
  eCommerceEvents,
  isEmployeePromotionBlock = false,
}: PromotionBlockContainerProps) => {
  const navigate = useNavigate();
  const products: ProductGridProduct[] = [];
  const companyInfo = useSessionData().companyInfo;
  const { authenticatedUser, isAuthenticated } = useAuth();
  const { fetchHeadersByCodes, priceAlteredModels, isHeaderCodeResolved } = useOnlineModelsContext();
  const promotionOnlineModelHeaders = useMemo(
    () =>
      promotions.reduce<OnlineModelHeader[]>((headers, promotion) => {
        const onlineModelHeader = onlineModelHeaders.find(omh => omh.onlineModelCode === promotion.productGuid);
        if (onlineModelHeader) {
          headers.push(onlineModelHeader);
        }
        return headers;
      }, []),
    [onlineModelHeaders, promotions]
  );

  useEffect(() => {
    if (promotionOnlineModelHeaders.length) {
      fetchHeadersByCodes(promotionOnlineModelHeaders.map(header => header.onlineModelCode));
    }
  }, [fetchHeadersByCodes, promotionOnlineModelHeaders]);

  getFilteredHeaders(
    promotionOnlineModelHeaders,
    companyInfo,
    authenticatedUser,
    priceAlteredModels?.onlineModelHeadersByCode
  ).map(omh => {
    const promotionText = promotions.find(p => p.productGuid === omh?.onlineModelCode)?.promotionText;
    const productGridProduct = isEmployeePromotionBlock
      ? employeePromotionToProductGridProduct(authenticatedUser, omh)
      : toProductGridProduct(
          authenticatedUser,
          omh,
          promotionText,
          priceAlteredModels?.onlineModelHeadersByCode,
          true,
          isAuthenticated ? !isHeaderCodeResolved(omh.onlineModelCode) : false
        );
    products.push(productGridProduct);
  });

  const onPromotionClick = (element: HTMLElement, event: React.SyntheticEvent) => {
    // This is not optimal because we are now tied to CL element structure and if that changes, this will break
    const id = element.getAttribute('aria-labelledby');
    const onlineModelHeader = promotionOnlineModelHeaders.find(omh => {
      return `${omh?.onlineModelCode}Name` === id;
    });
    const position = onlineModelHeader ? promotionOnlineModelHeaders.indexOf(onlineModelHeader) : 0;
    if (eCommerceEvents?.onClickListProduct && onlineModelHeader) {
      eCommerceEvents.onClickListProduct('PromotionBlock', [position], [onlineModelHeader]);
    }
    if (onlineModelHeader?.pagePath) {
      // Do not use the link click to navigate as that causes a full page reload
      event.preventDefault();
      navigate(onlineModelHeader.pagePath);
    }
  };

  const onImpression = (id: string) => {
    const onlineModelHeader = promotionOnlineModelHeaders.find(omh => {
      return omh?.onlineModelCode === id;
    });
    const position = onlineModelHeader ? promotionOnlineModelHeaders.indexOf(onlineModelHeader) : 0;
    if (eCommerceEvents?.onSeeListProduct && onlineModelHeader) {
      eCommerceEvents.onSeeListProduct('PromotionBlock', [position], [onlineModelHeader]);
    }
  };

  useIdObserver(
    products.map(p => p.productId),
    onImpression
  );

  return (
    <div className="of-promotion-block">
      <SiteTemplates.PromotionBlock title={title} products={products} onPromotionClick={onPromotionClick} />
    </div>
  );
};
